import { generatePath, useMatches, type UIMatch } from "react-router-dom";
import {
  RISK_ROUTE_CHARTS_MITIGATION_SEGMENT,
  RISK_ROUTE_CHARTS_OVERALL_SEGMENT,
  RISK_ROUTE_CHARTS_MAJOR_SEGMENT,
  RISK_ROUTE_CHARTS_MAJOR,
  RISK_ROUTE_CHARTS_MITIGATION,
  RISK_ROUTE_CHARTS_OVERALL,
} from "constants/routing";

import type { NavTabsHandle } from "components/navbar/types";
import { getRiskChartsPath, parseWellIdNameParam } from "helpers/routing";
import type { RiskPathOption } from "helpers/routing";

const riskPathOptions = new Map<string, RiskPathOption>([
  [RISK_ROUTE_CHARTS_OVERALL, RISK_ROUTE_CHARTS_OVERALL_SEGMENT],
  [RISK_ROUTE_CHARTS_MAJOR, RISK_ROUTE_CHARTS_MAJOR_SEGMENT],
  [RISK_ROUTE_CHARTS_MITIGATION, RISK_ROUTE_CHARTS_MITIGATION_SEGMENT],
]);

export const useNavTabsFromHandle = () => {
  const matches = useMatches() as Array<UIMatch<unknown, NavTabsHandle>>;

  const navTabsMatches = matches
    .filter((match) => match.handle)
    .filter((handleMatch) => handleMatch.handle.navTabs);

  const matchWithPrimaryHandle = navTabsMatches.find(
    (match) => match.handle.navTabs.primaryTabs,
  );

  const matchWithSecondaryHandle = navTabsMatches.find(
    (match) => match.handle.navTabs.secondaryTabs,
  );

  const withOutcomeScenarios = navTabsMatches.some(
    (match) => match.handle.navTabs.withOutcomeScenarios,
  );

  const primaryTabs = matchWithPrimaryHandle?.handle.navTabs.primaryTabs;
  // assumes secondary tabs
  const secondaryTabs =
    matchWithSecondaryHandle?.handle.navTabs.secondaryTabs?.map((tab) => {
      const wellIdName = matchWithSecondaryHandle.params.wellIdName ?? "";
      const riskPathOption = riskPathOptions.get(tab.link ?? "");

      if (riskPathOption && wellIdName) {
        const [hierarchyId, wellName] = parseWellIdNameParam(wellIdName);

        return {
          ...tab,
          link: getRiskChartsPath(
            Number(hierarchyId),
            wellName,
            riskPathOption,
          ),
        };
      }

      return {
        ...tab,
        link: generatePath(`${tab.link}/${wellIdName}`),
      };
    });

  return {
    primaryTabs,
    secondaryTabs,
    withOutcomeScenarios,
    primaryDefaultTab:
      primaryTabs?.find((tab) =>
        matches.some((m) => m.pathname.startsWith(tab.link ?? "")),
      )?.id ?? "",
    secondaryDefaultTab:
      secondaryTabs?.find((tab) =>
        matches.some((m) => m.pathname.startsWith(tab.link ?? "")),
      )?.id ?? "",
  };
};
